import { formatAppDateTime } from "./app-time.js";

function sourceName(source) {
  if (source.label) return source.label;
  const lakes = (source.lakes ?? []).map((lake) => lake[0].toUpperCase() + lake.slice(1));
  const location = lakes.length > 1
    ? `Lakes ${lakes.slice(0, -1).join(", ")} & ${lakes.at(-1)}`
    : lakes[0] && !lakes[0].startsWith("All ") ? `Lake ${lakes[0]}` : lakes[0] || "";
  return `${location || "NOAA model"} · ${source.model}`;
}

function sourceDetail(source) {
  const response = source.statusCode === null
    ? `No HTTP response${source.error ? ` · ${source.error}` : ""}`
    : `HTTP ${source.statusCode}${source.reason ? ` ${source.reason}` : ""}`;
  const parts = [response];
  if (Number.isFinite(source.responseTimeMs)) parts.push(`${source.responseTimeMs} ms`);
  const message = source.message || (source.statusCode !== null && !source.up ? source.error : "");
  if (message && !parts.includes(message)) parts.push(message);
  return parts.join(" · ");
}

export function startPublicStatus() {
  const panel = document.querySelector("#public-status");
  const checked = document.querySelector("#public-status-checked");
  const overview = document.querySelector("#public-status-overview");
  const list = document.querySelector("#public-status-list");
  if (!panel || !checked || !overview || !list) return;

  let savedPayload;
  try {
    savedPayload = JSON.parse(panel.dataset.snapshot || "null");
  } catch {
    savedPayload = null;
  }

  const copy = {
    up: "All NOAA data sources are operational",
    degraded: "Some NOAA data sources are unavailable",
    down: "NOAA data sources are unavailable",
  };

  function render(payload, refreshFailed = false) {
    if (!payload || !Array.isArray(payload.models)) return false;

    overview.className = `public-status-overview public-status-overview--${payload.status}`;
    const headline = document.createElement("strong");
    headline.textContent = copy[payload.status] || "NOAA data source status";
    const unavailable = payload.models.filter((source) => !source.up).length;
    const summary = unavailable ? null : document.createElement("span");
    if (summary) summary.textContent = "All checked sources responded successfully.";
    overview.replaceChildren(...[headline, summary].filter(Boolean));

    const rows = payload.models.map((source) => {
      const item = document.createElement("li");
      item.className = `public-status-item public-status-item--${source.up ? "up" : "down"}`;
      const name = document.createElement("strong");
      name.textContent = sourceName(source);
      const state = document.createElement("span");
      state.className = "public-status-state";
      state.textContent = source.up ? "Operational" : "Unavailable";
      const detail = document.createElement("small");
      detail.textContent = sourceDetail(source);
      item.append(name, state, detail);
      return item;
    });
    list.replaceChildren(...rows);

    const timestamp = Date.parse(payload.checkedAt);
    const checkedAt = Number.isFinite(timestamp)
      ? `Last checked ${formatAppDateTime(timestamp, { timeOptions: { timeZoneName: "short" } })}`
      : "Last saved response";
    checked.textContent = refreshFailed ? `${checkedAt} · live refresh failed; showing saved status` : checkedAt;
    return true;
  }

  render(savedPayload);

  async function refresh() {
    try {
      const response = await fetch(panel.dataset.endpoint || "/api/v1/status/sources", {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      const payload = await response.json();
      if (!response.ok && !Array.isArray(payload.models)) {
        throw new Error(`Status request failed (${response.status})`);
      }
      if (!render(payload)) throw new Error("Status response was incomplete");
      savedPayload = payload;
    } catch {
      if (savedPayload) render(savedPayload, true);
      else {
        checked.textContent = "Could not load the latest NOAA status.";
        overview.className = "public-status-overview public-status-overview--error";
        overview.replaceChildren(Object.assign(document.createElement("strong"), {
          textContent: "NOAA status is temporarily unavailable",
        }));
        list.replaceChildren(Object.assign(document.createElement("li"), {
          className: "public-status-item public-status-item--loading",
          textContent: "Please refresh this page to try again.",
        }));
      }
    }
  }

  void refresh();
  window.setInterval(() => { void refresh(); }, 60_000);
}
